import { plural } from '@lingui/core/macro'
import { useLingui } from '@lingui/react/macro'
import { Alert, Typography } from 'antd'
import { CantileverButton } from 'components/CantileverButton'
import { AnomalyData } from 'features/systemLens'
import { useScatterplotContext } from 'features/timeSeries'
import {
  AlertTriangle,
  PanelLeftClose,
  PanelLeftOpen,
  Sliders,
} from 'lucide-react'
import { DataProcessingToolbar } from 'pages/assetLens/components/tabs/timeSeriesAnalysis/DataProcessingToolbar'
import React from 'react'
import styled, { css } from 'styled-components'

const { Text } = Typography

const flexColumn = css`
  display: flex;
  flex-direction: column;
`

const flexCenter = css`
  display: flex;
  align-items: center;
  justify-content: center;
`

const Sidebar = styled.div<{ $collapsed: boolean }>`
  width: ${({ $collapsed }) => ($collapsed ? '2.5rem' : '20rem')};
  min-width: ${({ $collapsed }) => ($collapsed ? '2.5rem' : '20rem')};
  flex-shrink: 0;
  background: ${({ theme }) => theme.forecast.background2};
  border-right: ${({ theme }) => `1px solid ${theme.forecast.border}`};
  ${flexColumn};
  transition:
    width 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    min-width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  height: 100%;
  position: relative;
`

const SidebarHeader = styled.div<{ $collapsed?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${({ $collapsed }) =>
    $collapsed ? 'center' : 'space-between'};
  padding: 0.5rem;
  height: 2.25rem;
  border-bottom: 1px solid ${({ theme }) => theme.forecast.border};
  flex-shrink: 0;
  overflow: hidden;
  position: sticky;
  top: 0;
  background: ${({ theme }) => theme.forecast.background2};
  z-index: 1;
`

const SidebarContent = styled.div`
  padding: 0.75rem;
  height: 100%;
  width: 100%;
  flex: 1;
  display: flex;
  min-height: 0;
`

const CollapseButton = styled(CantileverButton)`
  background: transparent;
`

const CollapsedIndicator = styled.div`
  ${flexCenter};
  gap: 1rem;
  padding: 1rem 0;
  flex: 1;
  writing-mode: vertical-rl;
  text-orientation: mixed;
  text-transform: uppercase;

  svg {
    width: 1rem;
    height: 1rem;
  }
`

const ControlsSection = styled.div`
  padding-bottom: 1rem;
  ${flexColumn};
  height: 100%;
`

interface TimeSeriesSidebarProps {
  collapsed: boolean
  onToggleCollapse: () => void
  selectedSensorsCount: number
  anomalies: AnomalyData[]
  unitID: string | undefined
  unitName?: string
  siteID: string
}

export const TimeSeriesSidebar: React.FC<TimeSeriesSidebarProps> = ({
  collapsed,
  onToggleCollapse,
  anomalies,
}) => {
  const { t } = useLingui()
  const { dataLayers } = useScatterplotContext()
  const anomalyCount = anomalies.length

  return (
    <Sidebar $collapsed={collapsed}>
      <SidebarHeader $collapsed={collapsed}>
        {!collapsed && <Text>{t`DATA PROCESSING`}</Text>}
        <CollapseButton onClick={onToggleCollapse} iconOnly>
          {collapsed ? (
            <PanelLeftOpen width={'1.25rem'} height={'1.25rem'} />
          ) : (
            <PanelLeftClose width={'1.25rem'} height={'1.25rem'} />
          )}
        </CollapseButton>
      </SidebarHeader>

      {collapsed ? (
        <CollapsedIndicator>
          <Sliders />
          {t`Data Processing`}
        </CollapsedIndicator>
      ) : (
        <SidebarContent>
          <DataProcessingToolbar />

          {dataLayers.find((l) => l.id === 'anomalies')?.enabled &&
            anomalies.length > 0 && (
              <ControlsSection>
                <Alert
                  title={t`Active Anomalies`}
                  description={plural(anomalyCount, {
                    one: '# anomaly detected in current timeframe',
                    other: '# anomalies detected in current timeframe',
                  })}
                  type='warning'
                  showIcon
                  icon={<AlertTriangle size={16} />}
                />
              </ControlsSection>
            )}
        </SidebarContent>
      )}
    </Sidebar>
  )
}
